/**
 * OAuth Callback Page
 * Handles OAuth callback for platform connections (X, etc.)
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

type CallbackStatus = 'loading' | 'success' | 'error'

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<CallbackStatus>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    // Parse URL parameters
    const result = searchParams.get('status')
    const error = searchParams.get('error') || searchParams.get('message')
    const username = searchParams.get('username')

    // Small delay for UX
    const timer = setTimeout(() => {
      if (result === 'success') {
        setStatus('success')
        setMessage(`X Account Connected Successfully! @${username || 'Unknown'}`)
      } else {
        setStatus('error')
        setMessage(error || 'Connection failed. Please try again.')
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchParams])

  const handleGoBack = () => {
    navigate('/settings')
  }

  const handleRetry = () => {
    navigate('/settings')
  }

  return (
    <div className="min-h-screen bg-[#F8F8F9] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-[#FF2442] border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-[#333333] mb-2">
                Connecting Account...
              </h2>
              <p className="text-sm text-[#999999]">
                Please wait while we verify your connection
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                  <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-[#333333] mb-2">
                {message}
              </h2>
              <p className="text-sm text-[#999999] mb-6">
                Your X account has been linked to BeeChat
              </p>
              <div className="flex justify-center mb-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full">
                  <span className="text-xl">𝕏</span>
                  <span className="text-sm font-medium text-gray-700">X (Twitter)</span>
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <button
                onClick={handleGoBack}
                className="w-full py-3 bg-[#FF2442] text-white rounded-full text-base font-semibold hover:opacity-90 transition-all shadow-[0_4px_12px_rgba(255,36,66,0.2)]"
              >
                Back to Settings
              </button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-[#333333] mb-2">
                Connection Failed
              </h2>
              <p className="text-sm text-[#999999] mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="w-full py-3 bg-[#FF2442] text-white rounded-full text-base font-semibold hover:opacity-90 transition-all shadow-[0_4px_12px_rgba(255,36,66,0.2)]"
                >
                  Try Again
                </button>
                <button
                  onClick={handleGoBack}
                  className="w-full py-3 border border-[#EBEBEB] bg-transparent text-[#333333] rounded-full text-base font-medium hover:bg-gray-50 transition-colors"
                >
                  Back to Settings
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="text-center mt-6">
          <span className="text-sm text-[#999999]">Powered by </span>
          <span className="text-sm font-semibold text-[#FF2442]">BeeChat</span>
        </div>
      </div>
    </div>
  )
}

export default AuthCallback